// FILE: src/questionTypes/renderers.js
//
// Learner + review renderer components for the Phase-1 question types
// (Case Study Question Framework, PR 3). Attached to the registry as a
// side-effect import from questionTypes/index.js.
//
// Contract: LearnerRenderer receives { question, value, onChange, disabled }
// where `value` is the CANONICAL answer array for the type (see coreTypes)
// and onChange emits the next canonical array. ReviewRenderer receives
// { question, selected, correct } (both canonical arrays) and renders a
// read-only marked-up view.
//
// Accessibility: everything is keyboard-first — radios/checkboxes are native
// inputs, matching uses one labelled <select> per prompt, ordering uses
// Up/Down buttons (drag is deliberately NOT required anywhere).

import React from 'react';
import { attachRenderers } from './registry';

const T = {
  line: 'var(--line, #e5e8f1)', ink: 'var(--ink, #1a2138)', muted: 'var(--muted, #5a6582)',
  brand: 'var(--brand, #2c63ff)', brandSoft: 'var(--brand-soft, #eaf0ff)',
  good: 'var(--good, #16a571)', goodSoft: 'var(--good-soft, #e6f7ef)',
  bad: 'var(--bad, #d64545)', badSoft: 'var(--bad-soft, #fde9ea)',
};
const rowStyle = { display: 'flex', gap: 10, alignItems: 'flex-start', padding: '9px 12px',
  border: `1px solid ${T.line}`, borderRadius: 8, marginBottom: 8, cursor: 'pointer', fontSize: '0.9rem' };

// ── Choice list (multiple_choice / multiple_select / visual) ────────────────
function ChoiceRenderer({ question, value, onChange, disabled, multi }) {
  const selected = new Set(value || []);
  return (
    <div role="group" aria-label="Answer options">
      {question.image && question.image.src && (
        <img src={question.image.src} alt={question.image.alt || ''}
          style={{ maxWidth: '100%', borderRadius: 8, marginBottom: 10 }} />
      )}
      {(question.choices || []).map((c) => {
        const on = selected.has(c.id);
        return (
          <label key={c.id} style={{ ...rowStyle, background: on ? T.brandSoft : 'transparent',
            borderColor: on ? T.brand : T.line, opacity: disabled ? 0.7 : 1 }}>
            <input type={multi ? 'checkbox' : 'radio'} name={`q-${question.id}`} checked={on}
              disabled={disabled} data-testid={`answer-${question.id}-${c.id}`}
              onChange={() => {
                if (multi) {
                  const next = new Set(selected);
                  if (next.has(c.id)) next.delete(c.id); else next.add(c.id);
                  onChange(Array.from(next).sort());
                } else onChange([c.id]);
              }} />
            <span><strong>{c.id}.</strong> {c.text}</span>
          </label>
        );
      })}
    </div>
  );
}

// ── enhanced_matching: prompt blocks with a wrapped-option picker ────────────
//
// A native <select> cannot wrap long option text, so descriptive answers
// (engagement strategies, rationales) blew out the question panel and
// squeezed the prompt column into a sliver (Bryan, 2026-07-16). The chooser
// is now a minimal listbox: the trigger button shows the FULL selected text,
// wrapped; the popup lists options as full-width wrapped cards. Prompt sits
// above its chooser, so both get the panel's full width.

const matchOptStyle = (on) => ({
  display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px',
  borderRadius: 6, border: 'none', marginBottom: 2,
  background: on ? T.brandSoft : 'transparent', color: T.ink,
  fontSize: '0.85rem', lineHeight: 1.45, whiteSpace: 'normal', cursor: 'pointer',
});

function MatchOptionPicker({ questionId, prompt, options, value, disabled, onPick }) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef(null);
  React.useEffect(() => {
    if (!open) return undefined;
    function onDocDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);
  const selected = options.find((o) => String(o.id) === String(value)) || null;
  return (
    <div ref={rootRef} style={{ position: 'relative', width: '100%' }}
      onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}>
      <button type="button" disabled={disabled}
        aria-haspopup="listbox" aria-expanded={open}
        aria-label={`Match for: ${prompt.text}`}
        data-testid={`match-${questionId}-${prompt.id}`}
        style={{ width: '100%', textAlign: 'left', padding: '8px 34px 8px 12px', borderRadius: 8,
          border: `1px solid ${selected ? T.brand : T.line}`,
          background: selected ? T.brandSoft : 'transparent', color: selected ? T.ink : T.muted,
          fontSize: '0.86rem', lineHeight: 1.45, position: 'relative',
          cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.7 : 1 }}
        onClick={() => setOpen((o) => !o)}>
        {selected ? selected.text : 'Choose…'}
        <span aria-hidden="true" style={{ position: 'absolute', right: 12, top: '50%',
          transform: 'translateY(-50%)', color: T.muted, fontSize: '0.7rem' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div role="listbox" aria-label={`Options for: ${prompt.text}`}
          style={{ position: 'absolute', zIndex: 40, left: 0, right: 0, top: 'calc(100% + 4px)',
            background: 'var(--panel, #fff)', border: `1px solid ${T.line}`, borderRadius: 8,
            boxShadow: '0 8px 24px rgba(26,33,56,0.18)', maxHeight: 280, overflowY: 'auto', padding: 4 }}>
          {selected && (
            <button type="button" role="option" aria-selected="false"
              data-testid={`match-${questionId}-${prompt.id}-clear`}
              style={matchOptStyle(false)}
              onClick={() => { onPick(''); setOpen(false); }}>
              <em style={{ color: T.muted }}>Clear selection</em>
            </button>
          )}
          {options.map((o) => {
            const on = String(o.id) === String(value);
            return (
              <button key={o.id} type="button" role="option" aria-selected={on}
                data-testid={`match-${questionId}-${prompt.id}-${o.id}`}
                style={matchOptStyle(on)}
                onClick={() => { onPick(String(o.id)); setOpen(false); }}>
                {o.text}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MatchingRenderer({ question, value, onChange, disabled }) {
  const prompts = question.choices?.prompts || [];
  const options = question.choices?.options || [];
  const map = {};
  (value || []).forEach((pair) => {
    const [p, o] = String(pair).split(':');
    if (p && o) map[p] = o;
  });
  function set(pid, oid) {
    const next = { ...map };
    if (oid) next[pid] = oid; else delete next[pid];
    onChange(Object.entries(next).map(([p, o]) => `${p}:${o}`).sort());
  }
  return (
    <div>
      {prompts.map((p) => (
        <div key={p.id} style={{ ...rowStyle, cursor: 'default', display: 'block' }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>{p.text}</div>
          <MatchOptionPicker questionId={question.id} prompt={p} options={options}
            value={map[p.id] || ''} disabled={disabled} onPick={(oid) => set(p.id, oid)} />
        </div>
      ))}
    </div>
  );
}

// ── ordered_sequence: Up/Down buttons (keyboard-first, no drag needed) ──────
function OrderingRenderer({ question, value, onChange, disabled }) {
  const items = question.choices?.items || [];
  const byId = Object.fromEntries(items.map((i) => [String(i.id), i]));
  const order = (value && value.length ? value : items.map((i) => String(i.id)))
    .filter((id) => byId[id]);
  function move(idx, delta) {
    const j = idx + delta;
    if (j < 0 || j >= order.length) return;
    const next = [...order];
    const t = next[idx]; next[idx] = next[j]; next[j] = t;
    onChange(next);
  }
  return (
    <div role="list" aria-label="Arrange in order">
      {order.map((id, i) => (
        <div key={id} role="listitem" style={{ ...rowStyle, cursor: 'default', alignItems: 'center' }}>
          <span style={{ color: T.muted, fontWeight: 700, minWidth: 20 }}>{i + 1}.</span>
          <span style={{ flex: 1 }}>{byId[id].text}</span>
          <button type="button" disabled={disabled || i === 0} aria-label={`Move "${byId[id].text}" up`}
            data-testid={`order-up-${question.id}-${id}`}
            style={{ padding: '3px 10px', borderRadius: 6, border: `1px solid ${T.line}`, background: 'transparent', cursor: 'pointer' }}
            onClick={() => move(i, -1)}>↑</button>
          <button type="button" disabled={disabled || i === order.length - 1} aria-label={`Move "${byId[id].text}" down`}
            data-testid={`order-down-${question.id}-${id}`}
            style={{ padding: '3px 10px', borderRadius: 6, border: `1px solid ${T.line}`, background: 'transparent', cursor: 'pointer' }}
            onClick={() => move(i, +1)}>↓</button>
        </div>
      ))}
    </div>
  );
}

// ── Review renderers (read-only, marked) ────────────────────────────────────
function mark(isSel, isKey) {
  if (isKey && isSel) return { border: `2px solid ${T.good}`, background: T.goodSoft, badge: '✓ your answer (correct)' };
  if (isKey) return { border: `2px solid ${T.good}`, background: T.goodSoft, badge: '✓ correct answer' };
  if (isSel) return { border: `2px solid ${T.bad}`, background: T.badSoft, badge: '✕ your answer' };
  return { border: `1px solid ${T.line}`, background: 'transparent', badge: null };
}

function ChoiceReview({ question, selected, correct }) {
  const sel = new Set(selected || []); const key = new Set(correct || []);
  return (
    <div>
      {question.image && question.image.src && (
        <img src={question.image.src} alt={question.image.alt || ''} style={{ maxWidth: '100%', borderRadius: 8, marginBottom: 10 }} />
      )}
      {(question.choices || []).map((c) => {
        const m = mark(sel.has(c.id), key.has(c.id));
        return (
          <div key={c.id} style={{ ...rowStyle, cursor: 'default', border: m.border, background: m.background }}>
            <span style={{ flex: 1 }}><strong>{c.id}.</strong> {c.text}</span>
            {m.badge && <span style={{ fontSize: '0.72rem', fontWeight: 700, color: T.muted, whiteSpace: 'nowrap' }}>{m.badge}</span>}
          </div>
        );
      })}
    </div>
  );
}

function MatchingReview({ question, selected, correct }) {
  const prompts = question.choices?.prompts || [];
  const options = Object.fromEntries((question.choices?.options || []).map((o) => [String(o.id), o.text]));
  const selMap = Object.fromEntries((selected || []).map((p) => String(p).split(':')));
  const keyMap = Object.fromEntries((correct || []).map((p) => String(p).split(':')));
  return (
    <div>
      {prompts.map((p) => {
        const ok = selMap[p.id] === keyMap[p.id];
        return (
          <div key={p.id} style={{ ...rowStyle, cursor: 'default',
            border: ok ? `2px solid ${T.good}` : `2px solid ${T.bad}`,
            background: ok ? T.goodSoft : T.badSoft, display: 'block' }}>
            <div style={{ fontWeight: 700 }}>{p.text}</div>
            <div style={{ fontSize: '0.82rem' }}>Your match: {options[selMap[p.id]] || '—'}
              {!ok && <span style={{ color: T.muted }}> · Correct: {options[keyMap[p.id]] || '—'}</span>}</div>
          </div>
        );
      })}
    </div>
  );
}

function OrderingReview({ question, selected, correct }) {
  const byId = Object.fromEntries((question.choices?.items || []).map((i) => [String(i.id), i.text]));
  const sel = selected || []; const key = correct || [];
  return (
    <div>
      {sel.map((id, i) => {
        const ok = key[i] === id;
        return (
          <div key={`${id}-${i}`} style={{ ...rowStyle, cursor: 'default',
            border: ok ? `2px solid ${T.good}` : `2px solid ${T.bad}`,
            background: ok ? T.goodSoft : T.badSoft, alignItems: 'center' }}>
            <span style={{ fontWeight: 700, minWidth: 20 }}>{i + 1}.</span>
            <span style={{ flex: 1 }}>{byId[id] || id}</span>
            {!ok && <span style={{ fontSize: '0.74rem', color: T.muted }}>correct position: {key.indexOf(id) + 1 || '—'}</span>}
          </div>
        );
      })}
    </div>
  );
}

const SingleChoice = (p) => <ChoiceRenderer {...p} multi={false} />;
const MultiChoice = (p) => <ChoiceRenderer {...p} multi />;

attachRenderers('multiple_choice',       { supported: true, LearnerRenderer: SingleChoice, ReviewRenderer: ChoiceReview });
attachRenderers('visual_interpretation', { supported: true, LearnerRenderer: SingleChoice, ReviewRenderer: ChoiceReview });
attachRenderers('multiple_select',       { supported: true, LearnerRenderer: MultiChoice,  ReviewRenderer: ChoiceReview });
attachRenderers('enhanced_matching',     { supported: true, LearnerRenderer: MatchingRenderer, ReviewRenderer: MatchingReview });
attachRenderers('ordered_sequence',      { supported: true, LearnerRenderer: OrderingRenderer, ReviewRenderer: OrderingReview });
